import type { Locale } from "@iso27001/shared";

// English uses ISO rather than a US or British ordering: this is a compliance
// register read internationally, where 10/25/2024 and 25/10/2024 are both
// ambiguous but 2024-10-25 is not.
const TAG: Record<Locale, string> = { de: "de-DE", en: "en-CA" };

// Explicit fields rather than `dateStyle: "short"`: CLDR's "short" pattern
// renders a 2-digit year for de-DE ("25.10.24") on this ICU build, which is
// not what a compliance register should show. Pinning day/month/year keeps
// the year 4 digits in both locales, while Intl still chooses
// locale-idiomatic separators and, for the time, 24h vs. 12h notation.
const DATE_OPTS: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
const DATETIME_OPTS: Intl.DateTimeFormatOptions = { ...DATE_OPTS, hour: "2-digit", minute: "2-digit" };
// Audit-log rows are the one place elevated precision matters: bulk edits
// routinely land two entries in the same minute, and the log's job is to let
// a reader see the order on screen, not just store it correctly underneath.
const AUDIT_DATETIME_OPTS: Intl.DateTimeFormatOptions = { ...DATETIME_OPTS, second: "2-digit" };

/** A timestamp as a date, in the reader's language. */
export function formatDate(unixSeconds: number, locale: Locale): string {
  return new Intl.DateTimeFormat(TAG[locale], DATE_OPTS).format(new Date(unixSeconds * 1000));
}

/** A timestamp as a date and time, in the reader's language. */
export function formatDateTime(unixSeconds: number, locale: Locale): string {
  return new Intl.DateTimeFormat(TAG[locale], DATETIME_OPTS).format(new Date(unixSeconds * 1000));
}

/**
 * A timestamp as a date and time including seconds, in the reader's language.
 * For the audit trail specifically — see AUDIT_DATETIME_OPTS above.
 */
export function formatAuditTimestamp(unixSeconds: number, locale: Locale): string {
  return new Intl.DateTimeFormat(TAG[locale], AUDIT_DATETIME_OPTS).format(new Date(unixSeconds * 1000));
}

// Mantine's DateInput/DatePickerInput `valueFormat`, in dayjs tokens (not
// Intl — this controls what the picker's text field parses and displays, so
// it needs a literal pattern rather than a formatter function). English gets
// the same ISO ordering as formatDate's en-CA output above, for the same
// reason: unambiguous to a reader anywhere, not just US/UK convention.
const DATE_INPUT_FORMAT: Record<Locale, string> = { de: "DD.MM.YYYY", en: "YYYY-MM-DD" };

/** The date-picker input/display format for the reader's language. */
export function datePickerFormat(locale: Locale): string {
  return DATE_INPUT_FORMAT[locale];
}
